import React, { useState } from "react"
import dayjs from "dayjs"
import { Button, Card, Tag, Tooltip } from "antd"
import { DownOutlined, RightOutlined } from "@ant-design/icons"
import { faGear, faRankingStar } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getMeetPublishLabel } from "./SeasonMeetCard"

export interface ISharedAcceptedVirtualMeet {
  virtualMeetInvitationId: number
  virtualMeetId: number
  clubId: number
  acceptedMeetId: number | null
  meetName: string
  meetdate: string
  startdate: string | null
  course: number
  organizerClubId: number
  organizerClubName: string | null
  organizerClubNameLong: string | null
  pendingInvitations: number
  acceptedInvitations: number
  declinedInvitations: number
  removedInvitations: number
}

export interface ISharedVirtualMeetParticipantOverview {
  virtualMeetInvitationId: number
  virtualMeetId: number
  clubId: number
  status: "accepted" | "pending" | "declined" | "removed"
  seasonId: number | null
  seasonName: string | null
  poolId: number | null
  acceptedMeetId: number | null
  clubName: string | null
  clubNameLong: string | null
  leagueName: string | null
  meetstatus: number | null
  latestPublishedAt?: string | null
  publishCount?: number
}

const formatCourseLabel = (course: number | null | undefined): string => {
  if (course === 1) return "SCM"
  if (course === 2) return "LCM"
  return "SCY"
}

const getLegStatusMeta = (meetStatus: number | null | undefined) => {
  if (meetStatus === 2) {
    return { label: "Results Published", color: "#52c41a" }
  }

  if (meetStatus === 1) {
    return { label: "Heat Sheet Merged", color: "#1677ff" }
  }

  return { label: "Editing Entries", color: "#faad14" }
}

const getParticipantDisplayName = (
  participant: ISharedVirtualMeetParticipantOverview,
): string =>
  participant.clubNameLong ||
  participant.clubName ||
  `Club ${participant.clubId}`

const getMyClubStateCopy = (
  status: ISharedVirtualMeetParticipantOverview["status"],
): string => {
  if (status === "accepted") {
    return "Your club's leg of the meet is active in this virtual meet."
  }

  if (status === "pending") {
    return "Your club has been invited but has not completed setup yet."
  }

  if (status === "declined") {
    return "Your club previously declined this virtual meet invitation."
  }

  return "Your club was removed from this virtual meet."
}

const orderAcceptedParticipants = ({
  participants,
  organizerClubId,
  activeClubId,
}: {
  participants: ISharedVirtualMeetParticipantOverview[]
  organizerClubId: number
  activeClubId: number | null
}) => {
  const alphabeticalParticipants = [...participants].sort((left, right) =>
    getParticipantDisplayName(left).localeCompare(getParticipantDisplayName(right)),
  )

  const prioritizedClubIds = new Set<number>()
  const orderedParticipants: ISharedVirtualMeetParticipantOverview[] = []

  const organizerParticipant = alphabeticalParticipants.find(
    (participant) => participant.clubId === organizerClubId,
  )
  if (organizerParticipant) {
    orderedParticipants.push(organizerParticipant)
    prioritizedClubIds.add(organizerParticipant.clubId)
  }

  if (activeClubId) {
    const myParticipant = alphabeticalParticipants.find(
      (participant) => participant.clubId === activeClubId,
    )

    if (myParticipant && !prioritizedClubIds.has(myParticipant.clubId)) {
      orderedParticipants.push(myParticipant)
      prioritizedClubIds.add(myParticipant.clubId)
    }
  }

  return orderedParticipants.concat(
    alphabeticalParticipants.filter(
      (participant) => !prioritizedClubIds.has(participant.clubId),
    ),
  )
}

const getCompactVirtualSummary = ({
  acceptedCount,
  pendingCount,
  declinedCount,
  removedCount,
}: {
  acceptedCount: number
  pendingCount: number
  declinedCount: number
  removedCount: number
}) => {
  const parts = [
    acceptedCount > 0 ? `${acceptedCount} accepted` : null,
    pendingCount > 0 ? `${pendingCount} pending` : null,
    declinedCount > 0 ? `${declinedCount} declined` : null,
    removedCount > 0 ? `${removedCount} removed` : null,
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(" • ") : "No team responses yet"
}

export default function SharedAcceptedVirtualMeetCard({
  virtualMeet,
  participants,
  loadingParticipants,
  activeClubId,
  acceptedLegsExpanded,
  onToggleAcceptedLegs,
  onOpenMeet,
  onManageShell,
  onOpenVirtualResults,
  onOpenStatusBucket,
}: {
  virtualMeet: ISharedAcceptedVirtualMeet
  participants: ISharedVirtualMeetParticipantOverview[]
  loadingParticipants?: boolean
  activeClubId: number | null
  acceptedLegsExpanded: boolean
  onToggleAcceptedLegs?: () => void
  onOpenMeet?: (meetId: number) => void
  onManageShell?: (virtualMeetId: number) => void
  onOpenVirtualResults?: (virtualMeetId: number) => void
  onOpenStatusBucket?: (
    virtualMeetId: number,
    bucket: "pending" | "accepted" | "declined" | "removed",
  ) => void
}): JSX.Element {
  const [showAllTeams, setShowAllTeams] = useState(false)
  const acceptedParticipants = participants.filter(
    (participant) => participant.status === "accepted",
  )
  const orderedAcceptedParticipants = orderAcceptedParticipants({
    participants: acceptedParticipants,
    organizerClubId: virtualMeet.organizerClubId,
    activeClubId,
  })
  const pendingParticipants = participants.filter(
    (participant) => participant.status === "pending",
  )
  const declinedParticipants = participants.filter(
    (participant) => participant.status === "declined",
  )
  const removedParticipants = participants.filter(
    (participant) => participant.status === "removed",
  )
  const hasVirtualResults = acceptedParticipants.some(
    (participant) => participant.meetstatus === 2,
  )
  const myPendingParticipant = pendingParticipants.find(
    (participant) => participant.clubId === activeClubId,
  )
  const myDeclinedParticipant = declinedParticipants.find(
    (participant) => participant.clubId === activeClubId,
  )
  const myRemovedParticipant = removedParticipants.find(
    (participant) => participant.clubId === activeClubId,
  )
  const otherDeclinedParticipants = declinedParticipants.filter(
    (participant) => participant.clubId !== activeClubId,
  )
  const isOrganizer =
    activeClubId !== null && virtualMeet.organizerClubId === activeClubId
  const organizerName =
    virtualMeet.organizerClubNameLong || virtualMeet.organizerClubName
  const compactSummary = getCompactVirtualSummary({
    acceptedCount: virtualMeet.acceptedInvitations,
    pendingCount: virtualMeet.pendingInvitations,
    declinedCount: virtualMeet.declinedInvitations,
    removedCount: virtualMeet.removedInvitations,
  })

  return (
    <Card
      className="shared-virtual-meet-card season-accepted-virtual-card"
      variant="borderless"
      style={{
        minWidth: 0,
        borderRadius: 5,
        border: "1px solid rgba(0, 0, 0, 0.06)",
        borderLeft: "4px solid #1677ff",
        background:
          "linear-gradient(180deg, rgba(236, 246, 255, 0.72) 0%, rgba(255, 255, 255, 0.99) 100%)",
        boxShadow: "0 1px 2px 2px rgba(0, 0, 0, 0.04)",
      }}
      styles={{ body: { padding: "16px 18px" } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "#595959",
              marginBottom: 4,
            }}
          >
            Virtual Meet
          </div>
          <h3 style={{ margin: 0, color: "#101242" }}>{virtualMeet.meetName}</h3>
          <h4
            style={{
              marginTop: 8,
              marginBottom: 0,
              fontSize: 13,
              lineHeight: 1.55,
              fontWeight: 400,
              color: "rgba(16, 18, 66, 0.72)",
            }}
          >
            {virtualMeet.startdate ? (
              <span>
                Meet Window: {dayjs(virtualMeet.startdate).format("MM-DD-YYYY")} to {dayjs(virtualMeet.meetdate).format("MM-DD-YYYY")}
              </span>
            ) : (
              <span>{dayjs(virtualMeet.meetdate).format("MM-DD-YYYY")}</span>
            )}
            <br />
            <span>Virtual Course: {formatCourseLabel(virtualMeet.course)}</span>
            {organizerName ? (
              <span>
                <br />
                Organizer: {organizerName}
              </span>
            ) : null}
          </h4>
        </div>
        <div style={{ minWidth: 220, marginLeft: "auto" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            {isOrganizer && onManageShell ? (
              <Tooltip title="Manage virtual meet">
                <button
                  type="button"
                  onClick={() => onManageShell(virtualMeet.virtualMeetId)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    border: "1px solid rgba(16, 18, 66, 0.12)",
                    background: "#ffffff",
                    cursor: "pointer",
                    color: "#101242",
                  }}
                >
                  <FontAwesomeIcon icon={faGear} />
                </button>
              </Tooltip>
            ) : null}
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                color: "rgba(16, 18, 66, 0.62)",
                marginBottom: 6,
              }}
            >
              Virtual Meet Results
            </div>
            {onOpenVirtualResults ? (
              <div>
                <Button
                  type="primary"
                  disabled={!hasVirtualResults}
                  onClick={() => onOpenVirtualResults(virtualMeet.virtualMeetId)}
                >
                  <FontAwesomeIcon icon={faRankingStar} /> View Virtual Results
                </Button>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)" }}>
                {hasVirtualResults
                  ? "At least one accepted team has published results."
                  : "Results will appear here as accepted teams publish them."}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {([
          ["pending", "blue", virtualMeet.pendingInvitations],
          ["accepted", "green", virtualMeet.acceptedInvitations],
          ["declined", "default", virtualMeet.declinedInvitations],
          ["removed", "orange", virtualMeet.removedInvitations],
        ] as const).map(([bucket, color, count]) => (
          <Tag
            key={bucket}
            color={color}
            onClick={
              onOpenStatusBucket
                ? () => onOpenStatusBucket(virtualMeet.virtualMeetId, bucket)
                : undefined
            }
            style={{ cursor: onOpenStatusBucket ? "pointer" : "default" }}
          >
            {bucket.charAt(0).toUpperCase() + bucket.slice(1)} {count}
          </Tag>
        ))}
      </div>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: "#595959",
          }}
        >
          Accepted Teams
        </div>
        <button
          type="button"
          onClick={onToggleAcceptedLegs}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: "#1677ff",
            cursor: onToggleAcceptedLegs ? "pointer" : "default",
            userSelect: "none",
            background: "none",
            border: "none",
            padding: 0,
          }}
          disabled={!onToggleAcceptedLegs}
        >
          {acceptedLegsExpanded ? <DownOutlined /> : <RightOutlined />}
          <span>
            {acceptedLegsExpanded
              ? "Hide teams"
              : `Show teams (${acceptedParticipants.length})`}
          </span>
        </button>
      </div>

      {!acceptedLegsExpanded ? (
        <div style={{ marginTop: 12, fontSize: 12, color: "rgba(16, 18, 66, 0.72)" }}>
          {acceptedParticipants.length === 0
            ? "No teams have accepted this virtual meet yet."
            : `${acceptedParticipants.length} accepted team${acceptedParticipants.length === 1 ? "" : "s"}. Expand to review team status.`}
        </div>
      ) : loadingParticipants ? (
        <div style={{ marginTop: 12, fontSize: 12, color: "rgba(16, 18, 66, 0.62)" }}>
          Loading virtual meet team progress...
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          {acceptedParticipants.length === 0 ? (
            <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.62)" }}>
              No teams have accepted this virtual meet yet.
            </div>
          ) : (
            <>
              {(showAllTeams
                ? orderedAcceptedParticipants
                : orderedAcceptedParticipants.slice(0, 4)
              ).map((participant) => {
              const displayName = getParticipantDisplayName(participant)
              const statusMeta = getLegStatusMeta(participant.meetstatus)
              const isMyClub = participant.clubId === activeClubId
              const isClickableMyRow =
                isMyClub && Number.isInteger(participant.acceptedMeetId) && Boolean(onOpenMeet)
              const publishLabel =
                participant.meetstatus === 2
                  ? getMeetPublishLabel({
                      latestPublishedAt: participant.latestPublishedAt,
                      publishCount: participant.publishCount,
                    })
                  : null

              return (
                <div
                  key={`virtual_leg_${participant.virtualMeetInvitationId}`}
                  onClick={
                    isClickableMyRow
                      ? () => onOpenMeet?.(participant.acceptedMeetId as number)
                      : undefined
                  }
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    borderRadius: 10,
                    border: isClickableMyRow
                      ? "1px solid rgba(22, 119, 255, 0.24)"
                      : "1px solid rgba(16, 18, 66, 0.08)",
                    background: "rgba(255, 255, 255, 0.85)",
                    padding: "8px 10px",
                    cursor: isClickableMyRow ? "pointer" : "default",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#101242" }}>
                      {displayName}
                      {isMyClub ? (
                        <Tag color="geekblue" style={{ marginLeft: 8 }}>
                          My Club
                        </Tag>
                      ) : null}
                      {participant.clubId === virtualMeet.organizerClubId ? (
                        <Tag color="gold" style={{ marginLeft: 8 }}>
                          Organizer
                        </Tag>
                      ) : null}
                    </div>
                    {participant.seasonName ? (
                      <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)" }}>
                        Roster: {participant.seasonName}
                      </div>
                    ) : null}
                    {isMyClub ? (
                      <div
                        style={{
                          fontSize: 12,
                          color: "rgba(16, 18, 66, 0.82)",
                          marginTop: 4,
                        }}
                      >
                        {getMyClubStateCopy("accepted")}
                      </div>
                    ) : null}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: statusMeta.color,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span>{statusMeta.label}</span>
                      {publishLabel ? (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: "rgba(16, 18, 66, 0.62)",
                          }}
                        >
                          {publishLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })
              }
              {orderedAcceptedParticipants.length > 4 && (
                <button
                  type="button"
                  onClick={() => setShowAllTeams((v) => !v)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#1677ff",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: "4px 0",
                  }}
                >
                  {showAllTeams ? (
                    <><DownOutlined style={{ fontSize: 10 }} /> Show fewer teams</>
                  ) : (
                    <><RightOutlined style={{ fontSize: 10 }} /> Show all {orderedAcceptedParticipants.length} teams</>
                  )}
                </button>
              )}
            </>
          )}
          {myPendingParticipant ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                borderRadius: 10,
                border: "1px solid rgba(22, 119, 255, 0.18)",
                background: "rgba(230, 244, 255, 0.55)",
                padding: "8px 10px",
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: "#101242" }}>
                  {getParticipantDisplayName(myPendingParticipant)}
                  <Tag color="geekblue" style={{ marginLeft: 8 }}>
                    My Club
                  </Tag>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(16, 18, 66, 0.82)",
                    marginTop: 4,
                  }}
                >
                  {getMyClubStateCopy("pending")}
                </div>
              </div>
            </div>
          ) : null}
          {myDeclinedParticipant ? (
            <div
              style={{
                borderRadius: 10,
                border: "1px solid rgba(140, 140, 140, 0.18)",
                background: "rgba(245, 245, 245, 0.8)",
                padding: "8px 10px",
              }}
            >
              <div style={{ fontWeight: 700, color: "#101242" }}>
                {getParticipantDisplayName(myDeclinedParticipant)}
                <Tag color="geekblue" style={{ marginLeft: 8 }}>
                  My Club
                </Tag>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(16, 18, 66, 0.82)",
                  marginTop: 4,
                }}
              >
                {getMyClubStateCopy("declined")}
              </div>
            </div>
          ) : null}
          {myRemovedParticipant ? (
            <div
              style={{
                borderRadius: 10,
                border: "1px solid rgba(250, 140, 22, 0.18)",
                background: "rgba(255, 247, 230, 0.8)",
                padding: "8px 10px",
              }}
            >
              <div style={{ fontWeight: 700, color: "#101242" }}>
                {getParticipantDisplayName(myRemovedParticipant)}
                <Tag color="geekblue" style={{ marginLeft: 8 }}>
                  My Club
                </Tag>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(16, 18, 66, 0.82)",
                  marginTop: 4,
                }}
              >
                {getMyClubStateCopy("removed")}
              </div>
            </div>
          ) : null}
          {otherDeclinedParticipants.length > 0 ? (
            <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)" }}>
              Declined: {otherDeclinedParticipants
                .map(
                  (participant) =>
                    participant.clubNameLong || participant.clubName || `Club ${participant.clubId}`,
                )
                .join(", ")}
            </div>
          ) : null}
          <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)" }}>
            <strong>Teams</strong> {compactSummary}
          </div>
        </div>
      )}
    </Card>
  )
}